import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslate } from "react-polyglot";
import { Dialog, IconButton, makeStyles, Typography } from "@material-ui/core";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";

// Минимальный и максимальный масштаб изображения
const MIN_SCALE = 1;
const MAX_SCALE = 8;
// Шаг изменения масштаба за одно событие колеса мыши
const ZOOM_STEP = 1.15;

const useViewerStyles = makeStyles(() => ({
  paper: {
    backgroundColor: "rgba(0, 0, 0, 0.85)",
  },
  container: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    userSelect: "none",
    outline: "none",
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  closeButton: {
    position: "absolute",
    top: 12,
    right: 12,
    color: "white",
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "white",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.35)",
    },
  },
  navButtonPrev: {
    left: 12,
  },
  navButtonNext: {
    right: 12,
  },
  hint: {
    position: "absolute",
    bottom: 10,
    left: 0,
    right: 0,
    width: "fit-content",
    margin: "0 auto",
    padding: "4px 12px",
    borderRadius: 8,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    textAlign: "center",
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: 12,
    pointerEvents: "none",
  },
}));

/**
 * Попап полноэкранного просмотра изображения.
 *
 * Зум выполняется колесом мыши (точка под курсором остаётся на месте),
 * а когда изображение увеличено больше размера попапа, его можно
 * перемещать левой кнопкой мыши.
 *
 * Если передано несколько изображений, показываются кнопки навигации
 * для перехода к предыдущему/следующему изображению.
 *
 * @param {Boolean} open - Признак открытия попапа.
 * @param {String[]} images - Список URL изображений для просмотра и навигации.
 * @param {Number} [activeIndex] - Индекс текущего изображения в списке images.
 * @param {Function} [onNavigate] - Обработчик перехода к изображению, получает новый индекс.
 * @param {Function} onClose - Обработчик закрытия попапа.
 * @param {Object} rest - Остальные свойства, передаваемые в MUI Dialog.
 * @returns {*}
 * @constructor
 */
const ImageViewerDialog = ({ open, images, activeIndex = 0, onNavigate, onClose, ...rest }) => {
  const classes = useViewerStyles();
  const t = useTranslate();
  // Индекс текущего изображения с защитой от выхода за границы списка
  const safeIndex = images.length > 0 ? Math.min(Math.max(activeIndex, 0), images.length - 1) : 0;
  const hasMultipleImages = images.length > 1 && !!onNavigate;
  // Элемент контейнера хранится в состоянии (а не в ref), т.к. содержимое MUI-диалога
  // монтируется в DOM с задержкой, и эффекты по флагу open срабатывают раньше,
  // чем в ref появится элемент. Callback-ref вызывает перерендер при монтировании
  const [container, setContainer] = useState(/** @type {?HTMLDivElement} */ null);
  const imageRef = useRef(/** @type {?HTMLImageElement} */ null);
  // Состояние просмотра: масштаб и смещение изображения относительно центра попапа
  const [view, setView] = useState({ scale: MIN_SCALE, x: 0, y: 0 });
  // Признак выполняемого перетаскивания изображения левой кнопкой мыши
  const [isDragging, setDragging] = useState(false);
  // Данные активного перетаскивания: позиция курсора и смещение изображения на момент старта
  const dragRef = useRef(/** @type {?{x: Number, y: Number, offsetX: Number, offsetY: Number}} */ null);

  // Сброс масштаба и позиции при открытии попапа и переходе к другому изображению
  useEffect(() => {
    if (open) {
      setView({ scale: MIN_SCALE, x: 0, y: 0 });
      setDragging(false);
      dragRef.current = null;
    }
  }, [open, activeIndex]);

  /**
   * Ограничивает смещение изображения, чтобы его нельзя было увести
   * за пределы попапа. Актуально, когда изображение больше контейнера;
   * если изображение помещается целиком, смещение всегда обнуляется.
   *
   * @param {Number} x - Смещение по горизонтали.
   * @param {Number} y - Смещение по вертикали.
   * @param {Number} scale - Текущий масштаб.
   * @returns {{x: Number, y: Number}} Ограниченное смещение.
   */
  const clampOffset = useCallback(
    (x, y, scale) => {
      const image = imageRef.current;
      if (!container || !image) {
        return { x: 0, y: 0 };
      }
      // Размеры изображения (offsetWidth/Height не зависят от transform)
      const maxX = Math.max(0, (image.offsetWidth * scale - container.offsetWidth) / 2);
      const maxY = Math.max(0, (image.offsetHeight * scale - container.offsetHeight) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    [container],
  );

  // Нативный (не пассивный) обработчик колеса мыши, т.к. React 17 регистрирует
  // wheel как пассивный слушатель и preventDefault() внутри onWheel не срабатывает.
  // Подключается только когда контейнер реально смонтирован в DOM
  useEffect(() => {
    if (!open || !container) {
      return undefined;
    }
    /**
     * Зум изображения колесом мыши с сохранением точки под курсором.
     *
     * @param {WheelEvent} event
     */
    const handleWheel = (event) => {
      event.preventDefault();
      const rect = container.getBoundingClientRect();
      // Позиция курсора относительно центра контейнера
      const cursorX = event.clientX - rect.left - rect.width / 2;
      const cursorY = event.clientY - rect.top - rect.height / 2;
      const factor = event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      setView((prev) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor));
        if (scale === prev.scale) {
          return prev;
        }
        // Точка изображения (в координатах без масштаба), находящаяся под курсором
        const imageX = (cursorX - prev.x) / prev.scale;
        const imageY = (cursorY - prev.y) / prev.scale;
        // Новый сдвиг подбирается так, чтобы эта точка осталась под курсором после зума
        const clamped = clampOffset(cursorX - imageX * scale, cursorY - imageY * scale, scale);
        return { scale, ...clamped };
      });
    };
    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [open, container, clampOffset]);

  /**
   * Начало перетаскивания изображения левой кнопкой мыши.
   *
   * @param {MouseEvent} event
   */
  const handleMouseDown = (event) => {
    if (event.button !== 0) {
      return;
    }
    const image = imageRef.current;
    // Перетаскивание доступно, только когда изображение увеличено больше размера попапа
    const isLargerThanContainer =
      !!container &&
      !!image &&
      (image.offsetWidth * view.scale > container.offsetWidth + 1 ||
        image.offsetHeight * view.scale > container.offsetHeight + 1);
    if (!isLargerThanContainer) {
      return;
    }
    dragRef.current = { x: event.clientX, y: event.clientY, offsetX: view.x, offsetY: view.y };
    setDragging(true);
    event.preventDefault();
  };

  // Перемещение и отпускание кнопки мыши во время перетаскивания
  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }
    /**
     * Обновление смещения изображения при движении мыши.
     *
     * @param {MouseEvent} event
     */
    const handleMouseMove = (event) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      const nextX = drag.offsetX + event.clientX - drag.x;
      const nextY = drag.offsetY + event.clientY - drag.y;
      setView((prev) => ({ ...prev, ...clampOffset(nextX, nextY, prev.scale) }));
    };
    /** Завершение перетаскивания */
    const handleMouseUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, clampOffset]);

  const image = imageRef.current;
  // Изображение увеличено больше размера попапа — доступно перетаскивание
  const isPannable =
    !!image &&
    !!container &&
    (image.offsetWidth * view.scale > container.offsetWidth + 1 ||
      image.offsetHeight * view.scale > container.offsetHeight + 1);

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ className: classes.paper }} {...rest}>
      <div ref={setContainer} className={classes.container}>
        <img
          ref={imageRef}
          className={classes.image}
          src={images[safeIndex]}
          alt=""
          draggable={false}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            cursor: isPannable ? (isDragging ? "grabbing" : "grab") : "zoom-in",
          }}
          onMouseDown={handleMouseDown}
        />
        {hasMultipleImages && (
          <>
            <IconButton
              className={`${classes.navButton} ${classes.navButtonPrev}`}
              disabled={safeIndex === 0}
              onClick={() => onNavigate(safeIndex - 1)}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              className={`${classes.navButton} ${classes.navButtonNext}`}
              disabled={safeIndex === images.length - 1}
              onClick={() => onNavigate(safeIndex + 1)}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </>
        )}
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
        <Typography className={classes.hint}>{t("Exercises.type7.photoViewerHint")}</Typography>
      </div>
    </Dialog>
  );
};

ImageViewerDialog.propTypes = {
  activeIndex: PropTypes.number,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  onNavigate: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default ImageViewerDialog;
